/**
 * OpenFeature provider that wraps another (in our case, the
 * `FlagsmithClientProvider`) and consults the dev-tools overrides atom
 * before delegating.
 *
 * Why a wrapper instead of monkey-patching the inner provider?
 *  - The OpenFeature SDK caches resolution results per evaluation; we
 *    need a clean re-emit point (`ProviderEvents.ConfigurationChanged`)
 *    to make `useBooleanFlagValue` re-render after an override change.
 *  - We want to keep the upstream provider's behaviour (initialize,
 *    onContextChange, status events) bit-for-bit untouched in PROD —
 *    this wrapper is only ever installed in non-PROD builds.
 */
import {
  OpenFeatureEventEmitter,
  ProviderEvents,
  type EvaluationContext,
  type JsonValue,
  type Logger,
  type Provider,
  type ProviderMetadata,
  type ResolutionDetails,
} from '@openfeature/web-sdk';

import { recordFlagsmithKey } from '@/features/flags-dev/discovery';
import {
  getOverride,
  getVariantOverride,
  subscribeToOverrides,
  type FlagProvider,
} from '@/features/flags-dev/overrides';

const OVERRIDE_REASON = 'STATIC';

export class OverrideOpenFeatureProvider implements Provider {
  readonly metadata: ProviderMetadata;
  readonly runsOn = 'client' as const;
  readonly events: OpenFeatureEventEmitter = new OpenFeatureEventEmitter();
  readonly hooks?: Provider['hooks'];

  /** Provider key in the overrides map (always 'flagsmith' for now). */
  private readonly _providerKey: FlagProvider;
  private readonly _delegate: Provider;
  private readonly _unsubscribe: () => void;
  /**
   * Re-emit ConfigurationChanged when the *delegate* fires it (e.g. when
   * Flagsmith finishes hydrating its cache), so consumers behind us still
   * see the same lifecycle.
   */
  private _delegateUnsubscribe?: () => void;

  constructor(delegate: Provider, providerKey: FlagProvider = 'flagsmith') {
    this._delegate = delegate;
    this._providerKey = providerKey;
    this.metadata = {
      name: `Override(${delegate.metadata?.name ?? 'unknown'})`,
    };
    this.hooks = delegate.hooks;
    this._unsubscribe = subscribeToOverrides(() => {
      this.events.emit(ProviderEvents.ConfigurationChanged, {
        message: 'flag overrides changed (dev-tools)',
      });
    });

    const delegateEvents = (delegate as { events?: OpenFeatureEventEmitter }).events;
    if (delegateEvents) {
      const forward = () => {
        this.events.emit(ProviderEvents.ConfigurationChanged, {
          message: 'delegate ConfigurationChanged',
        });
      };
      delegateEvents.addHandler(ProviderEvents.ConfigurationChanged, forward);
      this._delegateUnsubscribe = () => {
        delegateEvents.removeHandler(ProviderEvents.ConfigurationChanged, forward);
      };
    }
  }

  async initialize(context?: EvaluationContext): Promise<void> {
    if (this._delegate.initialize) {
      await this._delegate.initialize(context);
    }
  }

  onContextChange(
    oldContext: EvaluationContext,
    newContext: EvaluationContext
  ): Promise<void> | void {
    return this._delegate.onContextChange?.(oldContext, newContext);
  }

  /** Pass-through accessor so dev-tools discovery can reach the wrapped Flagsmith client. */
  get flagsmithClient(): unknown {
    return (this._delegate as { flagsmithClient?: unknown }).flagsmithClient;
  }

  resolveBooleanEvaluation(
    flagKey: string,
    defaultValue: boolean,
    context: EvaluationContext,
    logger: Logger
  ): ResolutionDetails<boolean> {
    recordFlagsmithKey(flagKey);
    const override = getOverride(this._providerKey, flagKey);
    if (override) {
      return {
        value: override !== 'off',
        reason: OVERRIDE_REASON,
        variant: getVariantOverride(override) ?? override,
      };
    }
    return this._delegate.resolveBooleanEvaluation(
      flagKey,
      defaultValue,
      context,
      logger
    );
  }

  resolveStringEvaluation(
    flagKey: string,
    defaultValue: string,
    context: EvaluationContext,
    logger: Logger
  ): ResolutionDetails<string> {
    recordFlagsmithKey(flagKey);
    const override = getOverride(this._providerKey, flagKey);
    if (override) {
      const variant = getVariantOverride(override);
      if (variant !== undefined) {
        return {
          value: variant,
          reason: OVERRIDE_REASON,
          variant,
        };
      }
      const resolved = this._delegate.resolveStringEvaluation(
        flagKey,
        defaultValue,
        context,
        logger
      );
      return {
        ...resolved,
        value: override === 'on' ? (resolved.value ?? defaultValue) : defaultValue,
        reason: OVERRIDE_REASON,
        variant: override,
      };
    }
    return this._delegate.resolveStringEvaluation(flagKey, defaultValue, context, logger);
  }

  resolveNumberEvaluation(
    flagKey: string,
    defaultValue: number,
    context: EvaluationContext,
    logger: Logger
  ): ResolutionDetails<number> {
    recordFlagsmithKey(flagKey);
    const override = getOverride(this._providerKey, flagKey);
    if (override) {
      const resolved = this._delegate.resolveNumberEvaluation(
        flagKey,
        defaultValue,
        context,
        logger
      );
      return {
        ...resolved,
        value: override !== 'off' ? resolved.value : defaultValue,
        reason: OVERRIDE_REASON,
        variant: override,
      };
    }
    return this._delegate.resolveNumberEvaluation(flagKey, defaultValue, context, logger);
  }

  resolveObjectEvaluation<T extends JsonValue>(
    flagKey: string,
    defaultValue: T,
    context: EvaluationContext,
    logger: Logger
  ): ResolutionDetails<T> {
    recordFlagsmithKey(flagKey);
    const override = getOverride(this._providerKey, flagKey);
    if (override) {
      const resolved = this._delegate.resolveObjectEvaluation(
        flagKey,
        defaultValue,
        context,
        logger
      );
      return {
        ...resolved,
        value: override !== 'off' ? resolved.value : defaultValue,
        reason: OVERRIDE_REASON,
        variant: override,
      };
    }
    return this._delegate.resolveObjectEvaluation(flagKey, defaultValue, context, logger);
  }

  /**
   * Stop listening to override / delegate change events and forward to
   * the delegate's own onClose. The OpenFeature SDK calls this when the
   * provider is unset; safe to call manually too. Returns a Promise as
   * required by the OpenFeature `Provider` interface.
   */
  async onClose(): Promise<void> {
    this._unsubscribe();
    this._delegateUnsubscribe?.();
    if (this._delegate.onClose) {
      await this._delegate.onClose();
    }
  }
}
